import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../shared/api/api-client.ts'
import { ErrorState, LoadingState } from '../../shared/ui/query-state.tsx'
import { subscribeToAdminSessionExpired } from './admin-session-events.ts'
import { adminSessionQueryKey } from './admin-session.ts'

function adminLoginUrl(returnTo: string): string {
  return `/admin/entrar?returnTo=${encodeURIComponent(returnTo)}`
}

export function AdminSessionBoundary() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const returnTo = `${location.pathname}${location.search}${location.hash}`
  const session = useQuery({
    queryKey: adminSessionQueryKey,
    queryFn: api.getAdminSession,
    retry: false,
    refetchInterval: 4 * 60_000,
    refetchOnWindowFocus: true,
  })

  useEffect(
    () =>
      subscribeToAdminSessionExpired(() => {
        queryClient.removeQueries({ queryKey: adminSessionQueryKey })
        void navigate(adminLoginUrl(returnTo), { replace: true })
      }),
    [navigate, queryClient, returnTo],
  )

  useEffect(() => {
    if (!session.data) return
    const expiresAt = Math.min(
      new Date(session.data.session.expiresAt).getTime(),
      new Date(session.data.session.idleExpiresAt).getTime(),
    )
    const timeout = window.setTimeout(
      () => {
        queryClient.removeQueries({ queryKey: adminSessionQueryKey })
        void navigate(adminLoginUrl(returnTo), { replace: true })
      },
      Math.max(0, Math.min(expiresAt - Date.now(), 2_147_483_647)),
    )
    return () => window.clearTimeout(timeout)
  }, [navigate, queryClient, returnTo, session.data])

  if (session.isPending) {
    return (
      <section className="page-narrow admin-session-loading">
        <LoadingState label="Verificando a sessão administrativa…" />
      </section>
    )
  }

  if (session.error instanceof ApiError && session.error.status === 401) {
    return <Navigate to={adminLoginUrl(returnTo)} replace />
  }

  if (session.isError) {
    return (
      <section className="page-narrow admin-session-loading">
        <ErrorState retry={() => void session.refetch()}>
          Não foi possível confirmar a sessão administrativa.
        </ErrorState>
      </section>
    )
  }

  return <Outlet />
}
